import React, { useEffect, useMemo, useState } from "react";
import PocketBase from "pocketbase";
import Footer from "../components/footer";
import NavBar from "../components/navbar";

const pb = new PocketBase("https://friendsofstandrewsstatepark.org");

const IMAGE_FIELD_PRIORITY = ["image", "photo", "picture", "file", "files"];

function getFileName(record) {
  for (const field of IMAGE_FIELD_PRIORITY) {
    const value = record[field];
    if (Array.isArray(value) && value.length > 0) return value[0];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }

  for (const value of Object.values(record)) {
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "string"
    ) {
      return value[0];
    }
    if (
      typeof value === "string" &&
      /\.(png|jpe?g|webp|gif|avif|bmp|svg)$/i.test(value)
    ) {
      return value;
    }
  }

  return null;
}

function isExternalFile(fileName) {
  return /^https?:\/\//i.test(fileName) || fileName.startsWith("data:");
}

function getThumbUrl(record, fileName, orientation) {
  if (isExternalFile(fileName)) return fileName;

  const thumbByOrientation = {
    portrait: "200x300",
    landscape: "300x200",
    square: "200x200",
  };

  return pb.files.getUrl(record, fileName, {
    thumb: thumbByOrientation[orientation] || thumbByOrientation.square,
  });
}

function getProbeThumbUrl(record, fileName) {
  if (isExternalFile(fileName)) return fileName;
  // Tiny proportional thumb to cheaply determine portrait vs landscape.
  return pb.files.getUrl(record, fileName, { thumb: "40x0" });
}

function getFullUrl(record, fileName) {
  if (isExternalFile(fileName)) return fileName;
  return pb.files.getUrl(record, fileName);
}

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [orientationById, setOrientationById] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const records = await pb
          .collection("photos")
          .getFullList({ sort: "-created" });
        setPhotos(records);
      } catch (err) {
        setError("Unable to load photos right now. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = useMemo(
    () =>
      photos
        .map((record) => {
          const fileName = getFileName(record);
          if (!fileName) return null;

          return {
            id: record.id,
            record,
            fileName,
            probeSrc: getProbeThumbUrl(record, fileName),
            fullSrc: getFullUrl(record, fileName),
            title: (record.title || record.name || "").trim(),
            caption: record.description || record.caption || "",
          };
        })
        .filter(Boolean),
    [photos],
  );

  useEffect(() => {
    let isDisposed = false;

    cards.forEach((photo) => {
      if (orientationById[photo.id]) return;

      const probe = new Image();
      probe.onload = () => {
        if (isDisposed) return;
        const orientation =
          probe.naturalHeight > probe.naturalWidth
            ? "portrait"
            : probe.naturalWidth > probe.naturalHeight
              ? "landscape"
              : "square";

        setOrientationById((prev) => ({ ...prev, [photo.id]: orientation }));
      };
      probe.onerror = () => {
        if (isDisposed) return;
        setOrientationById((prev) => ({ ...prev, [photo.id]: "square" }));
      };
      probe.src = photo.probeSrc;
    });

    return () => {
      isDisposed = true;
    };
  }, [cards, orientationById]);

  return (
    <div className="content d-flex flex-column justify-content-between w-100 h-auto fsa-body-bg">
      <NavBar />
      <div className="d-flex flex-column flex-fill align-items-center px-3 pt-4 pb-5">
        <h1 className="fsa-section-title">Photo Gallery</h1>

        {loading && (
          <div className="h5 text-muted fst-italic">Loading photos...</div>
        )}
        {error && <div className="h5 text-danger">{error}</div>}

        {!loading && !error && cards.length === 0 && (
          <div className="h5 text-muted fst-italic">
            No photos available yet.
          </div>
        )}

        {!loading && !error && cards.length > 0 && (
          <div className="container-fluid px-0" style={{ maxWidth: 1200 }}>
            <div className="row g-3">
              {cards.map((photo) => (
                <div key={photo.id} className="col-6 col-md-4 col-lg-3 col-xl-2">
                  <div className="card gallery-card">
                    <button
                      type="button"
                      className="gallery-thumb-button"
                      onClick={() => setSelectedPhoto(photo)}
                      aria-label={`Open photo${photo.title ? `: ${photo.title}` : ""}`}
                    >
                      <img
                        src={getThumbUrl(
                          photo.record,
                          photo.fileName,
                          orientationById[photo.id] || "square"
                        )}
                        alt={photo.title || photo.caption || "Gallery photo"}
                        className="card-img-top gallery-image"
                        loading="lazy"
                      />
                    </button>
                    <div className="card-body">
                      {photo.title && <h5 className="card-title mb-2">{photo.title}</h5>}
                      {photo.caption && (
                        <p className="card-text mb-0">{photo.caption}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedPhoto && (
          <div
            className="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={selectedPhoto.title || "Gallery image"}
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="gallery-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn btn-sm btn-light gallery-lightbox-close"
                onClick={() => setSelectedPhoto(null)}
              >
                Close
              </button>
              <img
                src={selectedPhoto.fullSrc}
                alt={selectedPhoto.title || selectedPhoto.caption || "Gallery photo"}
                className="gallery-lightbox-image"
                loading="eager"
              />
              {selectedPhoto.title && <h4 className="mt-3 mb-1">{selectedPhoto.title}</h4>}
              {selectedPhoto.caption && <p className="mb-0">{selectedPhoto.caption}</p>}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
